import type { StateCreator } from 'zustand';
import type { Place, PlaceDetails } from '@my-fullstack-app/itinerary-types';
import { addMinutes, DEFAULT_START_TIME, nextDayStartTime, placeFromDetails, sortByStartTime, timeToMinutes } from '../helpers';
import type { ItineraryState } from '../itinerary-store';
import { markTripDirty } from '../sync-bridge';
import { track } from '../../lib/analytics';
import { moveDayItemByIndex, removeDayItem } from './day-item-helpers';

export interface PlaceSlice {
  addPlace: (tripId: string, dayId: string, placeDetails: PlaceDetails, targetStartTime?: string | undefined) => void;
  movePlace: (
    tripId: string,
    sourceDayId: string,
    sourceIndex: number,
    targetDayId: string,
    targetIndex: number
  ) => void;
  removePlace: (tripId: string, dayId: string, placeId: string) => void;
  updatePlaceStartTime: (
    tripId: string,
    dayId: string,
    placeId: string,
    startTime: string
  ) => void;
  updatePlaceEndTime: (
    tripId: string,
    dayId: string,
    placeId: string,
    endTime: string
  ) => void;
  /** Set or clear a user-defined display label for a place. Pass an
   *  empty string to clear (falls back to the Google `name`). */
  updatePlaceLabel: (
    tripId: string,
    dayId: string,
    placeId: string,
    customLabel: string
  ) => void;
  reorderPlace: (
    tripId: string,
    dayId: string,
    fromIndex: number,
    toIndex: number
  ) => void;
}

export const createPlaceSlice: StateCreator<ItineraryState, [], [], PlaceSlice> = (set, get) => ({
  addPlace: (tripId, dayId, placeDetails, targetStartTime) => {
    get()._capture();
    set((s) => {
      const trip = s.trips.find((t) => t.id === tripId);
      const day = trip?.days.find((d) => d.id === dayId);
      const startTime = targetStartTime ?? nextDayStartTime(day);

      // ADR-0044 field mapping (legacy + neutral) lives in the shared
      // `placeFromDetails` helper so wishlist add stays in lockstep.
      const place: Place = placeFromDetails(placeDetails, {
        startTime,
        endTime: addMinutes(startTime, 60),
      });

      return {
        trips: s.trips.map((t) => {
          if (t.id !== tripId) return t;
          return {
            ...t,
            days: t.days.map((d) => {
              if (d.id !== dayId) return d;
              return { ...d, places: sortByStartTime([...d.places, place]) };
            }),
          };
        }),
      };
    });
    markTripDirty(tripId, ['days']);
    track('place.added', {
      tripId,
      dayId,
      hasPhoto: !!placeDetails.photoRef,
      types: placeDetails.types?.slice(0, 3),
    });
  },

  movePlace: (tripId, sourceDayId, sourceIndex, targetDayId, targetIndex) => {
    // Place uses explicit index-based ordering — the user picks the
    // target index and we DON'T time-sort the destination, unlike
    // transport/custom whose invariant is start-time order.
    moveDayItemByIndex(set, get, 'places', tripId, sourceDayId, sourceIndex, targetDayId, targetIndex, {
      onMoved: ({ crossDay }) => track('place.moved', { tripId, crossDay }),
    });
  },

  removePlace: (tripId, dayId, placeId) => {
    removeDayItem(set, get, 'places', tripId, dayId, placeId);
  },

  updatePlaceStartTime: (tripId, dayId, placeId, startTime) => {
    set((s) => {
      const trip = s.trips.find((t) => t.id === tripId);
      const day = trip?.days.find((d) => d.id === dayId);
      const place = day?.places.find((p) => p.id === placeId);
      const oldStart = place?.startTime ?? DEFAULT_START_TIME;
      const oldEnd = place?.endTime ?? addMinutes(oldStart, 60);
      const durationMins = Math.max(30, timeToMinutes(oldEnd) - timeToMinutes(startTime));
      const newEnd = addMinutes(startTime, durationMins);
      return {
        trips: s.trips.map((t) => {
          if (t.id !== tripId) return t;
          return {
            ...t,
            days: t.days.map((d) => {
              if (d.id !== dayId) return d;
              return {
                ...d,
                places: sortByStartTime(
                  d.places.map((p) =>
                    p.id === placeId ? { ...p, startTime, endTime: newEnd } : p,
                  ),
                ),
              };
            }),
          };
        }),
      };
    });
    markTripDirty(tripId, ['days']);
  },

  updatePlaceEndTime: (tripId, dayId, placeId, endTime) => {
    set((s) => {
      const trip = s.trips.find((t) => t.id === tripId);
      const day = trip?.days.find((d) => d.id === dayId);
      const place = day?.places.find((p) => p.id === placeId);
      const start = place?.startTime ?? DEFAULT_START_TIME;
      const durationMins = Math.max(30, timeToMinutes(endTime) - timeToMinutes(start));
      const validEnd = addMinutes(start, durationMins);
      return {
        trips: s.trips.map((t) => {
          if (t.id !== tripId) return t;
          return {
            ...t,
            days: t.days.map((d) => {
              if (d.id !== dayId) return d;
              return {
                ...d,
                places: d.places.map((p) =>
                  p.id === placeId ? { ...p, endTime: validEnd } : p
                ),
              };
            }),
          };
        }),
      };
    });
    markTripDirty(tripId, ['days']);
  },

  updatePlaceLabel: (tripId, dayId, placeId, customLabel) => {
    const trimmed = customLabel.trim();
    set((s) => ({
      trips: s.trips.map((t) => {
        if (t.id !== tripId) return t;
        return {
          ...t,
          days: t.days.map((d) => {
            if (d.id !== dayId) return d;
            return {
              ...d,
              places: d.places.map((p) =>
                p.id === placeId
                  ? { ...p, customLabel: trimmed.length > 0 ? trimmed : undefined }
                  : p
              ),
            };
          }),
        };
      }),
    }));
    markTripDirty(tripId, ['days']);
  },

  reorderPlace: (tripId, dayId, fromIndex, toIndex) => {
    get()._capture();
    set((s) => {
      const trip = s.trips.find((t) => t.id === tripId);
      if (!trip) return s;

      const day = trip.days.find((d) => d.id === dayId);
      if (!day) return s;

      const places = [...day.places];
      const [removed] = places.splice(fromIndex, 1);
      if (removed != null) places.splice(toIndex, 0, removed);

      return {
        trips: s.trips.map((t) => {
          if (t.id !== tripId) return t;
          return {
            ...t,
            days: t.days.map((d) =>
              d.id === dayId ? { ...d, places } : d
            ),
          };
        }),
      };
    });
    markTripDirty(tripId, ['days']);
  },
});
